import React, { Component } from "react";
import PropTypes from "prop-types";
import {
    Typography,
    CardContent,
    CardActions,
    Card,
    Button
} from "material-ui";
import MobileStepper from "material-ui/MobileStepper";
import {
    updateUserSetupProperty,
    completeUserSetup
} from "../actions/userSetup.actions";
import PostEditor from "../components/PostEditor";
import { wrapComponent } from "../utils/container";
import { getExampleIntroduction, placeholderText } from "../utils/placeholder";
import KeyboardArrowLeft from "material-ui-icons/KeyboardArrowLeft";
import KeyboardArrowRight from "material-ui-icons/KeyboardArrowRight";
import { goBack, push, replace } from "react-router-redux";
import { grey } from "material-ui/colors/index";
import ChooseLanguage from "../components/ChooseLanguage";
import LanguageMultiSelect from "../components/LanguageMultiSelect";

const wellKnownQuestion = 1;
const learnLanguagesQuestion = 2;
const selfIntroduction = 3;

const stepSet = {
    [wellKnownQuestion]: true,
    [learnLanguagesQuestion]: true,
    [selfIntroduction]: true
};

const styleSheet = theme => ({
    mobileStepper: {
        flexGrow: 1
    },
    formControl: {
        margin: `${theme.spacing.unit}px 0`,
        minWidth: 120
    },
    cardContent: {
        width: `calc(100% - ${theme.spacing.unit * 4}px)`
    },
    container: {
        margin: `${theme.spacing.unit * 2}px auto`,
        width: "100%",
        maxWidth: 540
    },
    cardContainer: {
        minHeight: "calc(100vh - 200px)",
        margin: "0 auto",
        display: "flex",
        flex: 1,
        width: "100%",
        marginBottom: 70,
        alignItems: "vertical",
        justifyContent: "left"
    },
    flexTitle: {
        display: "flex",
        alignItems: "center"
    },
    feedTitle: {
        margin: `0 0 ${2 * theme.spacing.unit}px ${3 * theme.spacing.unit}px`,
        fontWeight: 300,
        color: grey[700],
        flex: 1
    },
    postEditorContainer: {
        margin: `${theme.spacing.unit}px 0`,
        width: "100%"
    },
    skipButton: {
        padding: 0,
        paddingBottom: 16,
        margin: 0,
        "&:hover": {
            backgroundColor: "transparent"
        }
    }
});

class SetupFlowPage extends Component {
    renderSelfIntroduction() {
        const { t, classes } = this.props;
        return (
            <CardContent className={classes.cardContent}>
                <Typography type="body1">
                    {t("setupFlow.Please write a short introduction")}
                </Typography>
                <ChooseLanguage
                    className={classes.formControl}
                    handleSelectLanguageChange={this.handleSelectLanguageChange}
                    language={this.props.userSetup.post.language}
                    learningLanguages={this.props.userSetup.languages
                        .filter(l => l.isLearning)
                        .map(l => l.language)}
                />
                <div className={classes.postEditorContainer}>
                    <PostEditor
                        rootRef={r => {
                            this.editor = r;
                        }}
                        text={this.props.userSetup.post.body}
                        onChange={this.onPostChange}
                        placeholder={t(placeholderText, {
                            exampleIntroduction: getExampleIntroduction(
                                this.props.userSetup.post.language
                            )
                        })}
                    />
                </div>
            </CardContent>
        );
    }

    componentWillReceiveProps(nextProps) {
        const { match } = this.props;
        let step = match.params.step;
        let nextStep = nextProps.match.params.step;

        // Autofocus the editor when it first renders.
        if (step !== nextStep && parseInt(nextStep, 10) === selfIntroduction) {
            this.updateLanguageIfNecessary();
            if (this.editor) {
                this.editor.focus();
            }
        }
        if (step !== nextStep) {
            setTimeout(() => {
                this.replaceWithMostValidStep();
            }, 0);
        }
    }

    updateLanguageIfNecessary = () => {
        const { userSetup } = this.props;

        if (
            userSetup.languages
                .filter(l => l.isLearning)
                .find(l => userSetup.post.language === l.language)
        ) {
            return;
        }

        const language = userSetup.languages.find(l => l.isLearning);

        if (language) {
            this.dispatch(
                updateUserSetupProperty({
                    post: userSetup.post.merge({ language: language.language })
                })
            );
        }
    };

    handleSelectLanguageChange = ({ target: { value } }) => {
        const { userSetup } = this.props;
        this.dispatch(
            updateUserSetupProperty({
                post: userSetup.post.merge({ language: value })
            })
        );
    };

    onPostChange = value => {
        const { userSetup } = this.props;
        this.dispatch(
            updateUserSetupProperty({
                post: userSetup.post.merge({ body: value })
            })
        );
    };

    dispatch = action => {
        this.props.dispatch(action);
    };

    handleSelectedLanguagesChanged = languages => {
        this.dispatch(updateUserSetupProperty({ languages }));
    };

    renderKnowWellLanguagesQuestion() {
        const { t } = this.props;
        const { languages } = this.props.userSetup;
        return (
            <CardContent>
                <Typography type="body1">
                    {t(
                        "editProfileDialog.What language or languages do you know well?"
                    )}
                </Typography>
                <LanguageMultiSelect
                    selectedLanguages={languages}
                    onChange={this.handleSelectedLanguagesChanged}
                    isLearning={false}
                />
            </CardContent>
        );
    }

    renderLearnLanguagesQuestion() {
        const { t } = this.props;
        const { languages } = this.props.userSetup;
        return (
            <CardContent>
                <Typography type="body1">
                    {t(
                        "editProfileDialog.What language(s) do you want to practice?"
                    )}
                </Typography>
                <LanguageMultiSelect
                    selectedLanguages={languages}
                    onChange={this.handleSelectedLanguagesChanged}
                    isLearning={true}
                />
            </CardContent>
        );
    }

    getActiveStep() {
        const { match } = this.props;
        let step = match.params.step;
        if (stepSet[step]) {
            return parseInt(step, 10);
        }
        return wellKnownQuestion;
    }

    renderQuestion() {
        const activeStep = this.getActiveStep();
        switch (activeStep) {
            case wellKnownQuestion:
                return this.renderKnowWellLanguagesQuestion();
            case learnLanguagesQuestion:
                return this.renderLearnLanguagesQuestion();
            case selfIntroduction:
            default:
                return this.renderSelfIntroduction();
        }
    }

    handleBack = () => {
        this.dispatch(goBack());
    };

    handleNext = () => {
        const activeStep = this.getActiveStep();

        if (activeStep === selfIntroduction) {
            this.dispatch(completeUserSetup());
        } else {
            this.dispatch(push(`/setup/step/${activeStep + 1}`));
        }
    };

    onSkipIntroduction = () => {
        this.dispatch(completeUserSetup({ skipIntroduction: true }));
    };

    introductionIsValid() {
        const { post } = this.props.userSetup;
        let body = post.body.trim();
        return !(body === "" || body.length > 4000);
    }

    isStepValid(step) {
        const { languages } = this.props.userSetup;

        switch (step) {
            case wellKnownQuestion:
                return languages.find(l => !l.isLearning);
            case learnLanguagesQuestion:
                return languages.find(l => l.isLearning);
            case selfIntroduction:
                return this.introductionIsValid();
            default:
                return true;
        }
    }

    replaceWithMostValidStep() {
        let activeStep = this.getActiveStep();
        let step = wellKnownQuestion;
        while (step !== activeStep) {
            if (!this.isStepValid(step)) {
                this.dispatch(replace("/setup/step/" + step));
                break;
            }
            step += 1;
        }
    }

    isActiveStepValid() {
        return this.isStepValid(this.getActiveStep());
    }

    getNextText() {
        const activeStep = this.getActiveStep();
        const { t } = this.props;

        switch (activeStep) {
            case selfIntroduction:
                return t("setupFlow.Finish");
            default:
                return t("setupFlow.Next");
        }
    }

    renderTitle() {
        const { classes, t, theme } = this.props;
        const activeStep = this.getActiveStep();

        let skipButton;
        if (activeStep === selfIntroduction) {
            skipButton = (
                <Button
                    className={classes.skipButton}
                    color="primary"
                    onClick={this.onSkipIntroduction}>
                    {t("setupFlow.Skip")}
                    {theme.direction === "rtl"
                        ? <KeyboardArrowLeft />
                        : <KeyboardArrowRight />}
                </Button>
            );
        }

        return (
            <div className={classes.flexTitle}>
                <Typography
                    className={classes.feedTitle}
                    component="h3"
                    type="body1">
                    {t("setupFlow.Account Setup", {
                        step: activeStep,
                        total: 3
                    })}
                </Typography>
                {skipButton}
            </div>
        );
    }
    render() {
        const activeStep = this.getActiveStep();
        const { classes, theme, t } = this.props;
        return (
            <div className={classes.container}>
                {this.renderTitle()}
                <Card elevation={1} className={classes.cardContainer}>
                    {this.renderQuestion()}
                    <CardActions>
                        <MobileStepper
                            type="progress"
                            steps={3}
                            activeStep={activeStep - 1}
                            className={classes.mobileStepper}
                            nextButton={
                                <Button
                                    dense
                                    onClick={this.handleNext}
                                    disabled={!this.isActiveStepValid()}>
                                    {this.getNextText()}
                                    {theme.direction === "rtl"
                                        ? <KeyboardArrowLeft />
                                        : <KeyboardArrowRight />}
                                </Button>
                            }
                            backButton={
                                <Button
                                    dense
                                    onClick={this.handleBack}
                                    disabled={activeStep === wellKnownQuestion}>
                                    {theme.direction === "rtl"
                                        ? <KeyboardArrowRight />
                                        : <KeyboardArrowLeft />}
                                    {t("setupFlow.Back")}
                                </Button>
                            }
                        />
                    </CardActions>
                </Card>
            </div>
        );
    }
}

SetupFlowPage.propTypes = {
    dispatch: PropTypes.func.isRequired,
    classes: PropTypes.object.isRequired,
    theme: PropTypes.object.isRequired,
    userSetup: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired
};

function mapStateToProps({ userSetup }) {
    return {
        userSetup
    };
}

export default wrapComponent(SetupFlowPage, styleSheet, mapStateToProps, true);



// WEBPACK FOOTER //
// ./src/containers/SetupFlowPage.jsx